import { useRef, useEffect } from "react";
import { Layers, LayoutGrid, Palette, Share2, Waves } from "lucide-react";
import { useDomainWarp } from "./DomainWarpContext";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";

export function DomainWarpControls() {
    const {
        canvasW,
        canvasH,
        sampleStep,
        seed,
        noiseScale,
        warpScale,
        warpStrength,
        warpIterations,
        octaves,
        contourLevels,
        baseThreshold,
        contourStep,
        fillColor,
        strokeColor,
        bgColor,
        strokeWidth,
        fillShapes,
        setCanvasW,
        setCanvasH,
        setSampleStep,
        setSeed,
        setNoiseScale,
        setWarpScale,
        setWarpStrength,
        setWarpIterations,
        setOctaves,
        setContourLevels,
        setBaseThreshold,
        setContourStep,
        setFillColor,
        setStrokeColor,
        setBgColor,
        setStrokeWidth,
        setFillShapes,
        applyPreset,
        getPresetValues,
        resetParameters,
        randomizeParameters,
        canExport,
        buildExportSvgString,
        handleDownload,
    } = useDomainWarp();

    const fillColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const strokeColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );
    const bgColorTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (fillColorTimeout.current)
                clearTimeout(fillColorTimeout.current);
            if (strokeColorTimeout.current)
                clearTimeout(strokeColorTimeout.current);
            if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
        };
    }, []);

    const handleFillColorPicker = (value: string) => {
        if (fillColorTimeout.current) clearTimeout(fillColorTimeout.current);
        fillColorTimeout.current = setTimeout(() => setFillColor(value), 150);
    };
    const handleStrokeColorPicker = (value: string) => {
        if (strokeColorTimeout.current)
            clearTimeout(strokeColorTimeout.current);
        strokeColorTimeout.current = setTimeout(
            () => setStrokeColor(value),
            150,
        );
    };
    const handleBgColorPicker = (value: string) => {
        if (bgColorTimeout.current) clearTimeout(bgColorTimeout.current);
        bgColorTimeout.current = setTimeout(() => setBgColor(value), 150);
    };

    const handleInt =
        (setter: (v: number) => void, min = 1) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls domain-warp-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Domain Warp</h2>
                    <ToolDescriptionPopover dialogLabel="Domain Warp tool description">
                        <p>
                            Feeds a Perlin noise field back into itself one to
                            three times, producing swirling, geological
                            iso-contours. More iterations and higher warp
                            strength create increasingly complex, turbulent
                            forms.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        disabled={!canExport}
                        onClick={handleDownload}
                    >
                        Download SVG
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="domain-warp"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Artboard" icon={LayoutGrid}>
                    <div className="field-row">
                        <label className="field-label">
                            Width (px)
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={4000}
                                step={50}
                                value={canvasW}
                                onChange={handleInt(setCanvasW, 100)}
                                debounceMs={500}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Height (px)
                            <DebouncedInput
                                type="number"
                                min={100}
                                max={4000}
                                step={50}
                                value={canvasH}
                                onChange={handleInt(setCanvasH, 100)}
                                debounceMs={500}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Sample step (px) — lower = finer detail, slower
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={20}
                                step={1}
                                value={sampleStep}
                                onChange={handleInt(setSampleStep, 1)}
                                debounceMs={500}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Field" icon={Waves}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Noise scale
                            <DebouncedInput
                                type="number"
                                min={0.0001}
                                max={0.05}
                                step={0.0001}
                                value={noiseScale}
                                onChange={handleFloat(
                                    setNoiseScale,
                                    0.0001,
                                    0.05,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">Warp iterations</label>
                        <div
                            style={{
                                display: "flex",
                                gap: "4px",
                                marginTop: "4px",
                            }}
                        >
                            {[1, 2, 3].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    className="zoom-button"
                                    style={{
                                        flex: 1,
                                        fontWeight:
                                            warpIterations === n
                                                ? "bold"
                                                : "normal",
                                        opacity:
                                            warpIterations === n ? 1 : 0.55,
                                    }}
                                    onClick={() => setWarpIterations(n)}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Warp strength (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2000}
                                step={10}
                                value={warpStrength}
                                onChange={handleFloat(setWarpStrength, 0, 2000)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Warp scale
                            <DebouncedInput
                                type="number"
                                min={0.0001}
                                max={0.05}
                                step={0.0001}
                                value={warpScale}
                                onChange={handleFloat(
                                    setWarpScale,
                                    0.0001,
                                    0.05,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            FBM octaves
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={8}
                                step={1}
                                value={octaves}
                                onChange={handleInt(setOctaves, 1)}
                                debounceMs={300}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Contours" icon={Layers}>
                    <div className="field-row">
                        <label className="field-label">
                            Levels
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={20}
                                step={1}
                                value={contourLevels}
                                onChange={handleInt(setContourLevels, 1)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Base threshold (0–1)
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={0.99}
                                step={0.01}
                                value={baseThreshold}
                                onChange={handleFloat(
                                    setBaseThreshold,
                                    0.01,
                                    0.99,
                                )}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Contour step
                            <DebouncedInput
                                type="number"
                                min={0.005}
                                max={0.3}
                                step={0.005}
                                value={contourStep}
                                onChange={handleFloat(
                                    setContourStep,
                                    0.005,
                                    0.3,
                                )}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <CheckboxField
                            checked={fillShapes}
                            onChange={setFillShapes}
                            label="Fill shapes"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Fill color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={fillColor}
                                    onChange={(e) =>
                                        handleFillColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={fillColor}
                                    onChange={(v) => setFillColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokeColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background color
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={bgColor}
                                    onChange={(e) =>
                                        handleBgColorPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={bgColor}
                                    onChange={(v) => setBgColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={10}
                                step={0.25}
                                value={strokeWidth}
                                onChange={handleFloat(setStrokeWidth, 0, 10)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "domain-warp-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["domain-warp"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
